import { useEffect, useState, type PropsWithChildren } from 'react';
import { Page, StyleSheet, Image, Link, View, Font } from '@react-pdf/renderer';
import QRCode from 'qrcode';

import Footer from './Footer';
import Header from './Header';
import MetadataBox, { type MetadataField } from './MetadataBox';
import { PDF_COLORS, PDF_FONT_FAMILY } from './styles';

import { formatIsoDate } from '@/utils/date';
import { appFetch } from '@/lib/fetch';

// PDF fonts must be TTF/OTF — react-pdf's fontkit chokes on subsetted
// WOFF2 (see "Offset is outside the bounds of the DataView"). The
// matching `.woff2` files stay in `assets/fonts/` for the web app.
import InterRegular from '@/assets/fonts/Inter-Regular.ttf';
import InterMedium from '@/assets/fonts/Inter-Medium.ttf';
import InterBold from '@/assets/fonts/Inter-Bold.ttf';
import InterItalic from '@/assets/fonts/Inter-Italic.ttf';
import InterMediumItalic from '@/assets/fonts/Inter-MediumItalic.ttf';
import InterBoldItalic from '@/assets/fonts/Inter-BoldItalic.ttf';

const FONT_MAGIC_TTF = 0x00010000;
const FONT_MAGIC_OTF = 0x4f54544f;
const FONT_MAGIC_TRUE = 0x74727565;

const arrayBufferToBinaryString = (ab: ArrayBuffer): string => {
  const bytes = new Uint8Array(ab);
  const CHUNK = 0x8000;
  return Array.from({ length: Math.ceil(bytes.length / CHUNK) }, (_, i) =>
    String.fromCharCode(...Array.from(bytes.subarray(i * CHUNK, (i + 1) * CHUNK)))
  ).join('');
};

const loadFontDataUri = async (url: string): Promise<string> => {
  const response = await appFetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`PDF font fetch failed (${response.status}): ${url}`);
  }
  const arrayBuffer = await response.arrayBuffer();
  if (arrayBuffer.byteLength < 4) {
    throw new Error(`PDF font response too short (${arrayBuffer.byteLength}B): ${url}`);
  }
  const magic = new DataView(arrayBuffer).getUint32(0, false);
  if (magic !== FONT_MAGIC_TTF && magic !== FONT_MAGIC_OTF && magic !== FONT_MAGIC_TRUE) {
    throw new Error(`PDF font invalid signature 0x${magic.toString(16).padStart(8, '0')}: ${url}`);
  }
  return `data:font/ttf;base64,${btoa(arrayBufferToBinaryString(arrayBuffer))}`;
};

let fontRegistrationPromise: Promise<void> | null = null;

export const ensureFontsRegistered = (): Promise<void> => {
  if (fontRegistrationPromise) return fontRegistrationPromise;
  const promise = (async () => {
    const [regular, medium, bold, italic, mediumItalic, boldItalic] = await Promise.all([
      loadFontDataUri(InterRegular),
      loadFontDataUri(InterMedium),
      loadFontDataUri(InterBold),
      loadFontDataUri(InterItalic),
      loadFontDataUri(InterMediumItalic),
      loadFontDataUri(InterBoldItalic),
    ]);
    Font.register({
      family: 'Inter',
      fonts: [
        { src: regular, fontWeight: 300 },
        { src: regular, fontWeight: 400 },
        { src: medium, fontWeight: 500 },
        { src: bold, fontWeight: 700 },
        { src: italic, fontWeight: 300, fontStyle: 'italic' },
        { src: italic, fontWeight: 400, fontStyle: 'italic' },
        { src: mediumItalic, fontWeight: 500, fontStyle: 'italic' },
        { src: boldItalic, fontWeight: 700, fontStyle: 'italic' },
      ],
    });
  })().catch((err) => {
    fontRegistrationPromise = null;
    throw err;
  });
  fontRegistrationPromise = promise;
  return promise;
};

Font.registerEmojiSource({
  format: 'png',
  url: 'https://cdn.jsdelivr.net/npm/emoji-datasource-apple@15.1.2/img/apple/64/',
  withVariationSelectors: true,
});

const styles = StyleSheet.create({
  body: {
    fontFamily: PDF_FONT_FAMILY,
    paddingTop: 0,
    paddingBottom: 80,
    backgroundColor: PDF_COLORS.pageBackground,
  },
  titleBlock: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 30,
    paddingTop: 18,
    paddingBottom: 14,
    gap: 20,
  },
  titleColumn: {
    flex: 1,
    paddingRight: 10,
  },
  documentTitle: {
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 1.3,
    color: PDF_COLORS.link,
    textDecoration: 'underline',
  },
  qrFrame: {
    width: 72,
    height: 72,
  },
});

interface CustomPageProps {
  data: {
    url: string;
    id: string | number;
    title: string;
    published_at: string;
    type: string;
  };
  relevancy?: {
    industries?: Array<string>;
    locations?: Array<string>;
    technologies?: Array<string>;
  };
  metadata?: {
    locations?: Array<string>;
    industries?: Array<string>;
    extra?: MetadataField[];
  };
}

const buildPrimary = (
  id: string | number,
  publishedAt: string,
  relevancy?: CustomPageProps['relevancy']
): MetadataField[] => {
  const relevantTo: string[] = [];
  if (relevancy) {
    relevantTo.push(...(relevancy.industries ?? []));
    relevantTo.push(...(relevancy.locations ?? []));
    relevantTo.push(...(relevancy.technologies ?? []));
  }

  return [
    { label: 'ID', value: String(id ?? '') },
    { label: 'Date', value: formatIsoDate(publishedAt, true) },
    { label: 'Relevant to', value: relevantTo },
  ];
};

const buildSecondary = (type: string, metadata?: CustomPageProps['metadata']): MetadataField[] => [
  { label: 'Type', value: type },
  { label: 'Locations', value: metadata?.locations ?? [] },
  { label: 'Industries', value: metadata?.industries ?? [] },
  ...(metadata?.extra ?? []),
];

const CustomPage = ({
  children,
  data: { url, id, title, published_at, type },
  relevancy,
  metadata,
}: PropsWithChildren<CustomPageProps>) => {
  const [qrCode, setQRCode] = useState<string>('');

  useEffect(() => {
    QRCode.toDataURL(url, {}, (err, result) => {
      if (err) {
        throw err;
      }
      setQRCode(result);
    });
  }, [url]);

  return (
    <Page size="A4" style={styles.body}>
      <Header />

      <MetadataBox
        primary={buildPrimary(id, published_at, relevancy)}
        secondary={buildSecondary(type, metadata)}
      />

      <View style={styles.titleBlock}>
        <View style={styles.titleColumn}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link src={url} style={styles.documentTitle}>
            {`${id} | ${title}`}
          </Link>
        </View>
        {qrCode ? <Image src={qrCode} style={styles.qrFrame} /> : <View style={styles.qrFrame} />}
      </View>

      {children}

      <Footer />
    </Page>
  );
};

export default CustomPage;

// Re-export for callers that want to add custom metadata fields.
export type { MetadataField };
